// PhonePreview.tsx
// Phone notification preview components for iOS and Android

import React from "react";
import { BellDot, LinkIcon } from "lucide-react";

interface PhonePreviewIOSProps {
  appName?: string;
  header: string;
  subtitle?: string;
  body: string;
  link?: string;
  image?: { name: string; dataUrl: string } | null;
}

export const PhonePreviewIOS: React.FC<PhonePreviewIOSProps> = ({
  appName = "Your App",
  header,
  subtitle,
  body,
  link,
  image,
}) => {
  const now = new Date();
  
  return (
    <div className="w-full max-w-[360px] rounded-[22px] border bg-white/95 p-3 shadow-xl">
      <div className="mb-2 flex items-center justify-between px-1 text-[10px] text-muted-foreground">
        <span>
          {now.toLocaleDateString("en-US", {
            weekday: "short",
            month: "short",
            day: "numeric",
          })}
        </span>
        <span>
          {now.toLocaleTimeString("en-US", {
            hour: "2-digit",
            minute: "2-digit",
          })}
        </span>
      </div>
      
      <div className="flex items-center gap-2 px-1">
        <div className="flex h-5 w-5 items-center justify-center rounded-md overflow-hidden bg-white">
          <img 
            src="https://startupcity.hamburg/logos/startups/kalia_lab.png"
            alt={appName}
            className="h-full w-full object-contain"
          />
        </div>
        <div className="text-xs font-medium">{appName}</div>
      </div>
      
      <div className="mt-2 rounded-2xl border bg-white p-3">
        <div className="flex items-start gap-3">
          <div className="min-w-0 flex-1">
            <div className="text-sm font-semibold leading-snug">
              {header || <span className="text-muted-foreground">(title)</span>}
            </div>
            {subtitle && (
              <div className="mt-0.5 text-[12px] font-semibold leading-snug text-muted-foreground">
                {subtitle}
              </div>
            )}
            <div className="mt-1 text-[13px] font-normal leading-snug">
              {body || <span className="text-muted-foreground">(message)</span>}
            </div>
            {link && (
              <div className="mt-2 flex items-center gap-1 text-[11px] text-blue-600">
                <LinkIcon className="h-3 w-3" />
                <span className="truncate">{link}</span>
              </div>
            )}
          </div>
          {image?.dataUrl && (
            <div className="shrink-0 overflow-hidden rounded-lg border">
              <img
                src={image.dataUrl}
                alt={image.name || "image"}
                className="h-16 w-16 object-cover"
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

interface PhonePreviewAndroidProps {
  appName?: string;
  title: string;
  body: string;
  image?: { name: string; dataUrl: string } | null;
}

export const PhonePreviewAndroid: React.FC<PhonePreviewAndroidProps> = ({
  appName = "Your App",
  title,
  body,
  image,
}) => {
  return (
    <div className="w-full max-w-[340px] rounded-2xl border bg-white p-3 shadow-lg">
      <div className="flex items-center gap-2 mb-2 px-1">
        <div className="flex h-5 w-5 items-center justify-center rounded overflow-hidden bg-white">
          <img 
            src="https://startupcity.hamburg/logos/startups/kalia_lab.png"
            alt={appName}
            className="h-full w-full object-contain"
          />
        </div>
        <div className="text-xs font-medium">{appName}</div>
        <div className="ml-auto text-[10px] text-muted-foreground">now</div>
      </div>
      <div className="flex gap-3">
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold leading-tight">
            {title || <span className="text-muted-foreground">(title)</span>}
          </div>
          <div className="mt-1 text-xs font-normal leading-snug text-muted-foreground line-clamp-2">
            {body || <span>(body)</span>}
          </div>
        </div>
        {image?.dataUrl && (
          <div className="shrink-0">
            <img src={image.dataUrl} alt="preview" className="h-12 w-12 rounded object-cover" />
          </div>
        )}
      </div>
    </div>
  );
};